import { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";

const REFRESH_INTERVAL_SECONDS = 60;

interface RefreshCountdownProps {
  isVisible: boolean;
  dataUpdatedAt?: number;
  onRefresh?: () => void;
  isRefreshing?: boolean;
}

export const RefreshCountdown = ({ isVisible, dataUpdatedAt, onRefresh, isRefreshing }: RefreshCountdownProps) => {
  const [secondsUntilRefresh, setSecondsUntilRefresh] = useState(REFRESH_INTERVAL_SECONDS);

  useEffect(() => {
    if (!isVisible) return;

    const tick = () => {
      if (dataUpdatedAt) {
        const elapsed = Math.floor((Date.now() - dataUpdatedAt) / 1000);
        setSecondsUntilRefresh(Math.max(REFRESH_INTERVAL_SECONDS - elapsed, 0));
      } else {
        setSecondsUntilRefresh((prev) => (prev <= 1 ? REFRESH_INTERVAL_SECONDS : prev - 1));
      }
    };

    tick();
    const timer = setInterval(tick, 1000);

    return () => clearInterval(timer);
  }, [isVisible, dataUpdatedAt]);

  if (!isVisible) return null;

  const content = (
    <>
      <RefreshCw className={cn("h-4 w-4 text-genie-primary", isRefreshing && "animate-spin")} />
      <span className="text-sm text-genie-text-secondary">
        Refreshing in <span className="font-semibold text-genie-primary">{secondsUntilRefresh}s</span>
      </span>
    </>
  );

  if (onRefresh) {
    return (
      <button
        type="button"
        onClick={onRefresh}
        disabled={isRefreshing}
        title="Refresh now"
        className="flex items-center gap-2 px-3 py-2 bg-genie-background-muted rounded-lg border border-genie-border hover:bg-genie-background disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {content}
      </button>
    );
  }

  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-genie-background-muted rounded-lg border border-genie-border">
      {content}
    </div>
  );
};
